import type * as THREE from 'three';
import { createEmptyProgram, type CameraProgram } from './camera/types.ts';
import type { ContactPin } from './retarget/contacts.ts';

export type ViewMode = 'skeleton' | 'octa' | 'mesh';

/**
 * What drives the viewport camera: 'free' = OrbitControls (default),
 * 'program' = follow the camera cuts, any other string = look through the
 * camera with that id.
 */
export type CameraView = 'free' | 'program' | (string & {});
/**
 * Timeline frame rate. Historically limited to the Motive rates (60/120/240);
 * now any positive number so the timeline can follow a take's native rate
 * (e.g. Move.AI's 180). The sidebar toggle shows the static rates plus the
 * loaded take's native rate.
 */
export type Fps = number;
export type TimeUnit = 'seconds' | 'timecode';
export type AspectKey = 'free' | '16:9' | '9:16' | '1:1' | '4:3';
export type ResolutionKey = '720p' | '1080p' | '1440p' | '2160p';
export type VideoLayout = 'pip' | 'split-lr' | 'split-tb' | 'overlay';

/** Reference-video overlay synced to the mocap timeline. */
export interface VideoState {
  name: string | null;
  /** Frames to offset the video by, to line up its start with the mocap. */
  frameDelta: number;
  layout: VideoLayout;
  /** Opacity of the video layer (overlay mode). */
  opacity: number;
  /** Opacity of the 3D layer (overlay mode). */
  sceneOpacity: number;
  /** In overlay mode, whether the video sits above the 3D. */
  onTop: boolean;
  /**
   * With the 3D on top (overlay mode, !onTop), render the 3D background
   * transparent so the video shows through everywhere except the models
   * (greenscreen-style composite).
   */
  keyBackground: boolean;
  /** Burn the reference video (current layout/opacities) into MP4 exports. */
  includeInExport: boolean;
}

/** Ground shadow toggle + key-light direction (degrees). */
export interface LightingState {
  shadow: boolean;
  /** Ground-shadow darkness 0–1. */
  shadowOpacity: number;
  /** Around Y, 0–360. */
  azimuth: number;
  /** Above the horizon, 10–85. */
  elevation: number;
}

/** Aspect ratio value (width / height), or null for "fill the viewport". */
export function aspectRatioValue(key: AspectKey): number | null {
  switch (key) {
    case '16:9':
      return 16 / 9;
    case '9:16':
      return 9 / 16;
    case '1:1':
      return 1;
    case '4:3':
      return 4 / 3;
    default:
      return null;
  }
}

const RESOLUTION_SHORT_EDGE: Record<ResolutionKey, number> = {
  '720p': 720,
  '1080p': 1080,
  '1440p': 1440,
  '2160p': 2160,
};

/** Exact even export dimensions for an aspect + resolution, or null if free. */
export function exportDimensions(
  aspectKey: AspectKey,
  resKey: ResolutionKey
): { width: number; height: number } | null {
  const aspect = aspectRatioValue(aspectKey);
  if (aspect === null) return null;
  const short = RESOLUTION_SHORT_EDGE[resKey];
  let w: number;
  let h: number;
  if (aspect >= 1) {
    h = short;
    w = Math.round(short * aspect);
  } else {
    w = short;
    h = Math.round(short / aspect);
  }
  const even = (n: number) => n - (n % 2);
  return { width: even(w), height: even(h) };
}

/**
 * Central application state. UI and the render loop read from here; mutations go
 * through the small action helpers below so side effects stay in one place.
 */
export interface AppState {
  // Loaded mocap source (FBX group containing bones + animations).
  source: THREE.Object3D | null;
  rootBone: THREE.Object3D | null;

  viewMode: ViewMode;

  // Scene / camera staging.
  background: number;
  aspect: AspectKey;
  resolution: ResolutionKey;
  video: VideoState;
  /** Ground shadow + key-light direction. */
  lighting: LightingState;

  /**
   * Experimental: rescale every avatar's bones to the mocap's segment lengths
   * so joints land exactly on the actor's (meshes warp; see Retargeter).
   */
  matchProportions: boolean;

  // Animation. Multi-actor takes get one mixer per actor (so duplicate-named
  // tracks bind correctly); `mixer` is the primary, used for the shared playhead.
  mixer: THREE.AnimationMixer | null;
  mixers: THREE.AnimationMixer[];
  clip: THREE.AnimationClip | null;
  playing: boolean;

  // Timeline.
  fps: Fps;
  /** Frame rate declared by (or estimated from) the loaded take, if known. */
  nativeFps: number | null;
  /** GLB export bake rate; null = bake at the timeline fps. */
  glbExportFps: number | null;
  /** MP4 export frame rate; null = the timeline fps. */
  videoExportFps: number | null;
  totalFrames: number;
  currentFrame: number;
  startFrame: number;
  endFrame: number;
  timeUnit: TimeUnit;

  // Camera program (animated cameras + cuts). THREE objects stay module-local
  // in the director/viz; only JSON-shaped data lives here.
  cameras: CameraProgram;
  selectedCameraId: string | null;
  /** Index into the selected camera's keyframes, or null. */
  selectedKeyframe: number | null;
  cameraView: CameraView;

  // Contact pins (hand/foot IK targets with keyframed influence).
  contacts: ContactPin[];
  selectedContactId: string | null;

  /** "Fix broken fingers" strength (0 = off .. 1 = full healing). */
  fingerFix: number;

  // Export.
  exporting: boolean;
}

export const state: AppState = {
  source: null,
  rootBone: null,
  viewMode: 'skeleton',
  background: 0x14171c,
  aspect: 'free',
  resolution: '1080p',
  video: {
    name: null,
    frameDelta: 0,
    layout: 'pip',
    opacity: 1,
    sceneOpacity: 1,
    onTop: true,
    keyBackground: true,
    includeInExport: false,
  },
  lighting: { shadow: false, shadowOpacity: 0.35, azimuth: 34, elevation: 48 },
  matchProportions: false,
  mixer: null,
  mixers: [],
  clip: null,
  playing: false,
  fps: 60,
  nativeFps: null,
  glbExportFps: null,
  videoExportFps: null,
  totalFrames: 0,
  currentFrame: 0,
  startFrame: 0,
  endFrame: 0,
  timeUnit: 'seconds',
  cameras: createEmptyProgram(),
  selectedCameraId: null,
  selectedKeyframe: null,
  cameraView: 'free',
  contacts: [],
  selectedContactId: null,
  fingerFix: 0,
  exporting: false,
};

/** Subscribers notified whenever notify() is called, so the UI can re-render. */
type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notify(): void {
  for (const fn of listeners) fn();
}

/** Clip duration in seconds derived from frame count and selected fps. */
export function clipDurationSeconds(): number {
  return state.totalFrames / state.fps;
}

export function frameToSeconds(frame: number): number {
  return frame / state.fps;
}

export function hasAnimation(): boolean {
  return state.mixer !== null && state.clip !== null && state.totalFrames > 0;
}
